import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import {
  Email as EmailIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from '../services/axiosConfig';
import EmailEditor from './EmailEditor';
import EmailAttachmentField from './EmailAttachmentField';
import {
  buildAudienceEmailsEndpoint,
  canRetryEmailCampaign,
  createEmailCampaign,
  validateEmailAttachment,
} from '../utils/emailCampaigns';

const hasEmailContent = (html) => html && html.replace(/<[^>]+>/g, '').trim().length > 0;

const EmailDashboard = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [audienceEmails, setAudienceEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });

  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [attachment, setAttachment] = useState(null);

  // Form state for creating campaigns
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    htmlBody: '',
    audienceType: '',
    recipientEmails: []
  });

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (formData.audienceType) {
      fetchAudienceEmails(formData.audienceType);
    }
  }, [formData.audienceType]);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get('/api/email-campaigns');
      setCampaigns(response.data.campaigns);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setError('Failed to fetch campaigns');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/email-campaigns/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAudienceEmails = async (audienceType) => {
    try {
      const response = await axios.get(buildAudienceEmailsEndpoint(audienceType, dateFilter));
      setAudienceEmails(response.data.emails);
    } catch (error) {
      console.error('Error fetching audience emails:', error);
      setError('Failed to fetch audience emails');
    }
  };

  const handleCreateCampaign = async () => {
    try {
      setLoading(true);
      await createEmailCampaign(axios, formData, attachment);
      
      // Refresh campaigns list
      await fetchCampaigns();
      
      // Close dialog and reset form
      setCreateDialogOpen(false);
      setFormData({
        name: '',
        subject: '',
        htmlBody: '',
        audienceType: '',
        recipientEmails: []
      });
      setAttachment(null);
      setTemplateName('');
      
      setError(null);
    } catch (error) {
      console.error('Error creating campaign:', error);
      setError(error.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCampaign = async (campaignId) => {
    try {
      setLoading(true);
      await axios.post(`/api/email-campaigns/${campaignId}/send`);
      await fetchCampaigns();
      setError(null);
    } catch (error) {
      console.error('Error sending campaign:', error);
      setError('Failed to send campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        setLoading(true);
        await axios.delete(`/api/email-campaigns/${campaignId}`);
        await fetchCampaigns();
        setError(null);
      } catch (error) {
        console.error('Error deleting campaign:', error);
        setError('Failed to delete campaign');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !formData.htmlBody.trim()) {
      setError('Template name and email content are required');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/email-campaigns/templates', {
        name: templateName.trim(),
        subject: formData.subject,
        htmlBody: formData.htmlBody,
      });
      await fetchTemplates();
      setTemplateName('');
      setError(null);
    } catch (error) {
      console.error('Error saving template:', error);
      setError('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadTemplate = (template) => {
    setFormData({
      ...formData,
      subject: template.subject || formData.subject,
      htmlBody: template.html_body || '',
    });
  };

  const isFormValid = formData.name && formData.subject && hasEmailContent(formData.htmlBody) && formData.audienceType && formData.recipientEmails.length > 0;

  const handleViewAnalytics = async (campaignId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/email-campaigns/${campaignId}/analytics`);
      setAnalytics(response.data.analytics);
      setAnalyticsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'success';
      case 'draft': return 'default';
      case 'scheduled': return 'warning';
      case 'partial': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <SendIcon />;
      case 'draft': return <EmailIcon />;
      case 'scheduled': return <ScheduleIcon />;
      case 'partial': return <SendIcon />;
      case 'failed': return <DeleteIcon />;
      default: return <EmailIcon />;
    }
  };

  if (loading && campaigns.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Email Campaigns
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchCampaigns();
              fetchTemplates();
            }}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Campaign
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <EmailIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{campaigns.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Campaigns
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SendIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {campaigns.filter(c => c.status === 'sent').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sent Campaigns
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ScheduleIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {campaigns.filter(c => c.status === 'scheduled').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Scheduled
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <EmailIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{templates.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Saved Templates
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Campaigns Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Campaigns
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell>{campaign.name}</TableCell>
                    <TableCell>{campaign.subject}</TableCell>
                    <TableCell>
                      <Chip 
                        label={campaign.audience_type.replace('_', ' ')} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(campaign.status)}
                        label={campaign.status}
                        color={getStatusColor(campaign.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(campaign.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewAnalytics(campaign.id)}
                        disabled={!['sent', 'partial', 'failed'].includes(campaign.status)}
                      >
                        <AnalyticsIcon />
                      </IconButton>
                      {(campaign.status === 'draft' || canRetryEmailCampaign(campaign.status)) && (
                        <IconButton
                          size="small"
                          onClick={() => handleSendCampaign(campaign.id)}
                          title={campaign.status === 'draft' ? 'Send campaign' : 'Retry failed recipients'}
                        >
                          <SendIcon />
                        </IconButton>
                      )}
                      {campaign.status === 'draft' && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCampaign(campaign.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Campaign Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Email Campaign</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Campaign Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Subject Line"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              margin="normal"
            />

            {templates.length > 0 && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Load Saved Template</InputLabel>
                <Select
                  value=""
                  displayEmpty
                  onChange={(e) => {
                    const template = templates.find((item) => String(item.id) === e.target.value);
                    if (template) handleLoadTemplate(template);
                  }}
                >
                  <MenuItem value="" disabled>Select a template</MenuItem>
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={String(template.id)}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box sx={{ mt: 2, mb: 2 }}>
              <EmailEditor
                value={formData.htmlBody}
                onChange={(htmlBody) => setFormData({ ...formData, htmlBody })}
              />
            </Box>

            <EmailAttachmentField
              attachment={attachment}
              onChange={(file) => {
                const attachmentError = validateEmailAttachment(file);
                setError(attachmentError);
                setAttachment(attachmentError ? null : file);
              }}
            />

            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
              <TextField
                label="Save as template"
                placeholder="Template name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" onClick={handleSaveTemplate} disabled={!templateName.trim() || !formData.htmlBody.trim()}>
                Save Template
              </Button>
              <Button variant="text" onClick={() => setPreviewDialogOpen(true)} disabled={!formData.htmlBody.trim()}>
                Preview
              </Button>
            </Box>

            <FormControl fullWidth margin="normal">
              <InputLabel>Audience Type</InputLabel>
              <Select
                value={formData.audienceType}
                onChange={(e) => setFormData({ ...formData, audienceType: e.target.value })}
              >
                <MenuItem value="first_timers">First Timers</MenuItem>
                <MenuItem value="new_converts">New Converts</MenuItem>
                <MenuItem value="harvest_contacts">Harvest Contacts</MenuItem>
                <MenuItem value="all">All Contacts</MenuItem>
              </Select>
            </FormControl>
            {formData.audienceType && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Available emails for {formData.audienceType.replace('_', ' ')}: {audienceEmails.length}
                </Typography>
                
                {/* Date Filter Section */}
                <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Filter by Date Range (Optional)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={dateFilter.startDate}
                      onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ minWidth: 150 }}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={dateFilter.endDate}
                      onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ minWidth: 150 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => fetchAudienceEmails(formData.audienceType)}
                      sx={{ minWidth: 100 }}
                    >
                      Apply Filter
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setDateFilter({ startDate: '', endDate: '' });
                        fetchAudienceEmails(formData.audienceType);
                      }}
                      sx={{ minWidth: 100 }}
                    >
                      Clear Filter
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const allEmails = audienceEmails.map(item => item.email);
                      setFormData({ ...formData, recipientEmails: allEmails });
                    }}
                  >
                    Select All ({audienceEmails.length})
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setFormData({ ...formData, recipientEmails: [] })}
                  >
                    Clear All
                  </Button>
                </Box>
                
                <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  {audienceEmails.slice(0, 20).map((item, index) => (
                    <ListItem key={index} dense>
                      <ListItemText 
                        primary={item.email}
                        secondary={`${item.first_name} ${item.last_name}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => {
                            const isSelected = formData.recipientEmails.includes(item.email);
                            if (isSelected) {
                              setFormData({
                                ...formData,
                                recipientEmails: formData.recipientEmails.filter(email => email !== item.email)
                              });
                            } else {
                              setFormData({
                                ...formData,
                                recipientEmails: [...formData.recipientEmails, item.email]
                              });
                            }
                          }}
                          size="small"
                          color={formData.recipientEmails.includes(item.email) ? "error" : "primary"}
                        >
                          {formData.recipientEmails.includes(item.email) ? <DeleteIcon /> : <AddIcon />}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                  {audienceEmails.length > 20 && (
                    <ListItem>
                      <ListItemText 
                        primary={`... and ${audienceEmails.length - 20} more`}
                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}
            
            <Typography variant="subtitle2" gutterBottom>
              Selected Recipients ({formData.recipientEmails.length})
            </Typography>

            <List dense>
              {formData.recipientEmails.map((email, index) => (
                <ListItem key={index}>
                  <ListItemText primary={email} />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => setFormData({
                        ...formData,
                        recipientEmails: formData.recipientEmails.filter(e => e !== email)
                      })}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            {formData.recipientEmails.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No recipients selected yet
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateCampaign} 
            variant="contained"
            disabled={!isFormValid}
          >
            Send Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Email Preview</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            Subject: {formData.subject || '(no subject)'}
          </Typography>
          <Box
            sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
            dangerouslySetInnerHTML={{ __html: formData.htmlBody }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={analyticsDialogOpen} onClose={() => setAnalyticsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Campaign Analytics</DialogTitle>
        <DialogContent>
          {analytics && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">{analytics.recipients.total}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Recipients
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">{analytics.delivery?.sent ?? 0}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Successfully Sent
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">{analytics.delivery?.failed ?? 0}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Failed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">{analytics.delivery?.pending ?? 0}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pending
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              {analytics.note && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {analytics.note}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailDashboard;
